import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Mail, Lock, User as UserIcon, Phone, MailCheck, Eye, EyeOff, Hash } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { validateStorePassword, validateStoreUserId } from "@/lib/storeAssociate";

const AUTH_ERRORS = {
  user_already_exists:        "An account already exists with this email address. Please sign in instead.",
  invalid_credentials:        "Incorrect email or password.",
  store_invalid_credentials:  "Incorrect SA ID or password.",
  email_not_confirmed:        "Please confirm your email first — check your inbox for the link we sent you.",
  weak_password:              "Password must be at least 6 characters.",
  email_address_invalid:      "Please enter a valid email address.",
  over_email_send_rate_limit: "Too many attempts. Please wait a few minutes and try again.",
  over_request_rate_limit:    "Too many attempts. Please wait a few minutes and try again.",
  signup_disabled:            "New sign-ups are temporarily disabled.",
  user_not_found:             "No account found with this email.",
};

function authErrorMessage(err) {
  return AUTH_ERRORS[err?.code] || err?.message || "Something went wrong. Please try again.";
}

const EMAIL_RE  = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE  = /^[6-9]\d{9}$/;

const BLANK_FORM = { email: "", password: "", name: "", phone: "", dob: "", storeUserId: "" };

function validateSignup({ name, email, phone, dob, password }) {
  if (!name.trim())                    return "Full name is required.";
  if (!EMAIL_RE.test(email.trim()))    return "Enter a valid email address.";
  if (!PHONE_RE.test(phone.replace(/\s/g, "")))
                                       return "Enter a valid 10-digit Indian mobile number.";
  if (dob && new Date(dob) > new Date()) return "Date of birth cannot be in the future.";
  if (!password || password.length < 6) return "Password must be at least 6 characters.";
  return null;
}

function validateSignin({ email, password }) {
  if (!EMAIL_RE.test(email.trim()))    return "Enter a valid email address.";
  if (!password)                       return "Password is required.";
  return null;
}

export const AuthModal = ({ open, onOpenChange, defaultMode = "signin", storeOnly = false, onStoreSignIn }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { firebaseReady, signInEmail, signInStoreAssociate, signUpEmail, signInGoogle, resetPassword } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  const [associateMode, setAssociateMode] = useState(storeOnly);
  const [form, setForm] = useState(BLANK_FORM);
  const [loading, setLoading] = useState(false);
  const [fpError, setFpError] = useState("");
  const [signupSentTo, setSignupSentTo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const showAssociate = storeOnly || associateMode;

  useEffect(() => {
    if (open && storeOnly) setAssociateMode(true);
  }, [open, storeOnly]);

  const set = (k) => (v) => {
    if (k === "email") setFpError("");
    setForm((f) => ({ ...f, [k]: v }));
  };

  const resetModal = () => {
    setSignupSentTo(null);
    setMode("signin");
    if (!storeOnly) setAssociateMode(false);
    setForm(BLANK_FORM);
    setFpError("");
    setShowPassword(false);
  };

  const closeModal = (next) => {
    if (!next) resetModal();
    onOpenChange(next);
  };

  const goToAssociateLogin = () => {
    setAssociateMode(true);
    setMode("signin");
    setForm((f) => ({ ...f, password: "", storeUserId: "" }));
    setFpError("");
  };

  const goToCustomerLogin = () => {
    setAssociateMode(false);
    setForm((f) => ({ ...f, password: "", storeUserId: "" }));
    setFpError("");
  };

  const afterAssociateSignIn = () => {
    onOpenChange(false);
    resetModal();
    if (onStoreSignIn) onStoreSignIn();
    else navigate("/store", { replace: true });
    toast.success("Welcome to Store Billing");
  };

  const submitAssociate = async (e) => {
    e.preventDefault();
    const idErr = validateStoreUserId(form.storeUserId);
    const pwErr = validateStorePassword(form.password);
    if (idErr) { toast.error(idErr); return; }
    if (pwErr) { toast.error(pwErr); return; }
    setLoading(true);
    try {
      await signInStoreAssociate(form.storeUserId.trim(), form.password);
      afterAssociateSignIn();
    } catch (err) {
      toast.error(
        err?.code === "store_invalid_credentials"
          ? (err.message || "Incorrect SA ID or password.")
          : authErrorMessage(err)
      );
    } finally {
      setLoading(false);
    }
  };

  const submitCustomer = async (e) => {
    e.preventDefault();
    const isSignup = mode === "signup";
    const err = isSignup ? validateSignup(form) : validateSignin(form);
    if (err) { toast.error(err); return; }

    setLoading(true);
    try {
      if (isSignup) {
        const result = await signUpEmail(
          form.email.trim(),
          form.password,
          form.name.trim(),
          form.phone.replace(/\s/g, ""),
          form.dob,
        );
        setForm(BLANK_FORM);
        if (result?.session) {
          toast.success("Account created! Welcome to Annapurna Home Pickles.");
          onOpenChange(false);
          resetModal();
          navigate("/profile");
        } else {
          setSignupSentTo(form.email.trim());
        }
      } else {
        await signInEmail(form.email.trim(), form.password);
        setForm(BLANK_FORM);
        toast.success("Welcome back!");
        onOpenChange(false);
        resetModal();
      }
    } catch (err) {
      if (err?.code === "user_already_exists") {
        setMode("signin");
        setForm((f) => ({ email: f.email, password: "", name: "", phone: "", dob: "", storeUserId: "" }));
      }
      toast.error(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const google = async () => {
    setLoading(true);
    try {
      await signInGoogle();
      toast.success("Signed in with Google");
      onOpenChange(false);
      resetModal();
    } catch (err) {
      toast.error(authErrorMessage(err) || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const forgot = async () => {
    const email = form.email.trim();
    if (!EMAIL_RE.test(email)) {
      setFpError("Enter your email address first.");
      return;
    }
    setFpError("");
    setLoading(true);
    try {
      await resetPassword(email);
      toast.success("If that email is registered, we've sent a password reset link — check your inbox.");
      onOpenChange(false);
      resetModal();
    } catch (err) {
      setFpError(authErrorMessage(err) || "Could not send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode((m) => (m === "signin" ? "signup" : "signin"));
    setForm(BLANK_FORM);
    setFpError("");
  };

  if (!firebaseReady) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="auth-modal-not-configured" className="bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#5A2D1D]">Sign-in unavailable</DialogTitle>
            <DialogDescription className="text-[#2E1A12]/80">
              Customer accounts are powered by Firebase. The store owner hasn&apos;t connected Firebase yet.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  if (signupSentTo) {
    return (
      <Dialog open={open} onOpenChange={closeModal}>
        <DialogContent
          data-testid="auth-modal-signup-success"
          className="bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm max-w-md p-0 overflow-hidden"
        >
          <div className="p-8 sm:p-10 text-center">
            <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-[#556B2F]/15 grid place-items-center">
              <MailCheck className="w-8 h-8 text-[#556B2F]" />
            </div>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl sm:text-3xl text-[#5A2D1D] text-center">
                Check your email
              </DialogTitle>
              <DialogDescription className="text-[#2E1A12]/80 text-center">
                We&apos;ve sent a verification link to <b>{signupSentTo}</b>. Please verify your email to activate your account before signing in.
              </DialogDescription>
            </DialogHeader>
            <button
              data-testid="auth-signup-success-close"
              onClick={() => closeModal(false)}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] py-3 rounded-full font-semibold transition-colors"
            >
              Got it
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const isSignup = mode === "signup";

  return (
    <Dialog open={open} onOpenChange={closeModal}>
      <DialogContent
        data-testid={showAssociate ? "auth-modal-associate" : "auth-modal"}
        className="bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm max-w-md p-0 overflow-hidden"
      >
        <div className="p-6 sm:p-8">
          {showAssociate ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl sm:text-3xl text-[#5A2D1D]">
                  SA Login
                </DialogTitle>
                <DialogDescription className="text-[#2E1A12]/80">
                  Store associate sign in — use the ID and password from your admin.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={submitAssociate} noValidate className="space-y-3 mt-5" data-testid="auth-associate-form">
                <Field
                  icon={Hash}
                  type="text"
                  inputMode="numeric"
                  placeholder="SA ID * (2 digits)"
                  value={form.storeUserId}
                  onChange={(v) => setForm((f) => ({ ...f, storeUserId: v.replace(/\D/g, "").slice(0, 2) }))}
                  testid="auth-store-userid-input"
                  required
                  maxLength={2}
                />
                <Field
                  icon={Lock}
                  type={showPassword ? "text" : "password"}
                  placeholder="Password *"
                  value={form.password}
                  onChange={set("password")}
                  testid="auth-associate-password-input"
                  required
                  toggle={
                    <button
                      type="button"
                      data-testid="auth-password-toggle"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A2D1D]/50 hover:text-[#5A2D1D]"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  }
                />
                <button
                  data-testid="auth-associate-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#556B2F] hover:bg-[#6a8438] text-[#F5E6CC] py-3 rounded-full font-semibold transition-colors disabled:opacity-60"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Sign In
                </button>
              </form>

              {!storeOnly && (
                <button
                  type="button"
                  data-testid="auth-back-to-customer"
                  onClick={goToCustomerLogin}
                  className="mt-4 w-full text-sm font-semibold text-[#5A2D1D] hover:underline"
                >
                  ← Customer sign in
                </button>
              )}
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl sm:text-3xl text-[#5A2D1D]">
                  {isSignup ? t.auth.sign_up : t.auth.sign_in}
                </DialogTitle>
                <DialogDescription className="text-[#2E1A12]/80">
                  {isSignup ? t.auth.new_to : t.auth.have_account}
                </DialogDescription>
              </DialogHeader>

              <button
                data-testid="auth-google-button"
                onClick={google}
                disabled={loading}
                className="mt-5 w-full inline-flex items-center justify-center gap-3 bg-white border-2 border-[#5A2D1D]/20 hover:border-[#5A2D1D]/40 text-[#2E1A12] py-3 rounded-sm font-semibold transition-colors disabled:opacity-60"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
                  <path d="M16.51 8.19c0-.59-.05-1.16-.15-1.71H9v3.25h4.21c-.18.97-.74 1.79-1.57 2.34v1.95h2.54c1.49-1.37 2.33-3.39 2.33-5.83z" fill="#4285F4"/>
                  <path d="M9 17c2.12 0 3.9-.7 5.2-1.91l-2.54-1.95c-.7.47-1.6.74-2.66.74-2.04 0-3.78-1.38-4.4-3.24H2v2.01A8 8 0 0 0 9 17z" fill="#34A853"/>
                  <path d="M4.6 10.64a4.8 4.8 0 0 1 0-3.08V5.55H2a8 8 0 0 0 0 6.9l2.6-1.81z" fill="#FBBC05"/>
                  <path d="M9 4.78c1.15 0 2.18.39 2.99 1.16l2.24-2.24A8 8 0 0 0 2 5.55l2.6 2.01C5.22 6.16 6.96 4.78 9 4.78z" fill="#EA4335"/>
                </svg>
                {t.auth.continue_google}
              </button>

              <div className="flex items-center gap-3 my-5 text-xs uppercase tracking-widest text-[#556B2F] font-bold">
                <span className="flex-1 h-px bg-[#5A2D1D]/20" />
                {t.auth.or}
                <span className="flex-1 h-px bg-[#5A2D1D]/20" />
              </div>

              <form onSubmit={submitCustomer} className="space-y-3" data-testid="auth-form">
                {isSignup && (
                  <Field
                    icon={UserIcon}
                    placeholder="Full Name *"
                    value={form.name}
                    onChange={set("name")}
                    testid="auth-name-input"
                    required
                  />
                )}

                <Field
                  icon={Mail}
                  type="email"
                  autoComplete="email"
                  placeholder={t.auth.email + " *"}
                  value={form.email}
                  onChange={set("email")}
                  testid="auth-email-input"
                  required
                />

                {isSignup && (
                  <>
                    <Field
                      icon={Phone}
                      type="tel"
                      inputMode="numeric"
                      placeholder="Mobile Number * (10 digits)"
                      value={form.phone}
                      onChange={(v) => set("phone")(v.replace(/\D/g, ""))}
                      testid="auth-phone-input"
                      maxLength={10}
                      required
                    />
                    <div>
                      <label className="block text-xs font-semibold text-[#5A2D1D]/70 mb-1 ml-1">
                        Date of Birth (optional)
                      </label>
                      <input
                        data-testid="auth-dob-input"
                        type="date"
                        value={form.dob}
                        onChange={(e) => setForm((f) => ({ ...f, dob: e.target.value }))}
                        max={new Date().toISOString().split("T")[0]}
                        className="w-full px-4 py-3 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-[#2E1A12]"
                      />
                    </div>
                  </>
                )}

                <Field
                  icon={Lock}
                  type={showPassword ? "text" : "password"}
                  placeholder={t.auth.password + " *"}
                  value={form.password}
                  onChange={set("password")}
                  testid="auth-password-input"
                  required
                  toggle={
                    <button
                      type="button"
                      data-testid="auth-password-toggle"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A2D1D]/50 hover:text-[#5A2D1D]"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  }
                />

                <button
                  data-testid="auth-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] py-3 rounded-full font-semibold transition-colors disabled:opacity-60"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSignup ? t.auth.create : t.auth.sign_in}
                </button>

                {!isSignup && (
                  <button
                    type="button"
                    data-testid="auth-sa-login-button"
                    onClick={goToAssociateLogin}
                    disabled={loading}
                    className="hidden sm:inline-flex w-full items-center justify-center gap-2 border border-[#556B2F]/50 text-[#556B2F] hover:bg-[#556B2F]/10 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-60"
                  >
                    SA Login
                  </button>
                )}
              </form>

              {!isSignup && (
                <>
                  <button
                    data-testid="auth-forgot-button"
                    onClick={forgot}
                    disabled={loading}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#556B2F] font-semibold hover:text-[#5A2D1D] disabled:opacity-50"
                    type="button"
                  >
                    {loading && <Loader2 className="w-3 h-3 animate-spin" />}
                    {t.auth.forgot}
                  </button>
                  {fpError && (
                    <div className="mt-2 flex items-start gap-2 bg-[#EFD9B4] border border-[#5A2D1D]/40 text-[#5A2D1D] rounded-sm px-3 py-2 text-xs font-semibold">
                      <span className="mt-0.5 flex-shrink-0">⚠️</span>
                      <span>{fpError}</span>
                    </div>
                  )}
                </>
              )}

              <div className="mt-5 pt-4 border-t border-[#5A2D1D]/15 text-sm text-[#2E1A12]/80">
                {isSignup ? "Already have an account?" : "New here?"}{" "}
                <button
                  data-testid="auth-switch-mode"
                  onClick={switchMode}
                  className="text-[#5A2D1D] font-bold hover:underline"
                  type="button"
                >
                  {isSignup ? t.auth.sign_in : t.auth.create}
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ icon: Icon, testid, type = "text", value, onChange, placeholder, required, maxLength, inputMode, autoComplete, toggle }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A2D1D]/60" />
    <input
      data-testid={testid}
      type={type}
      inputMode={inputMode}
      autoComplete={autoComplete}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      maxLength={maxLength}
      className={`w-full pl-10 py-3 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-[#2E1A12] placeholder:text-[#2E1A12]/50 ${toggle ? "pr-10" : "pr-4"}`}
    />
    {toggle}
  </div>
);
